import React, { useRef, useEffect, useMemo } from 'react';
import { sanitizeHtml } from '../../../utils/sanitizeHtml';

interface HtmlContentWithCopyButtonsProps {
  html: string;
  className?: string;
  style?: React.CSSProperties;
}

const HtmlContentWithCopyButtons: React.FC<HtmlContentWithCopyButtonsProps> = ({ html, className, style }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // Stored task / feed HTML is not authored by us — sanitize before injecting.
  const safeHtml = useMemo(() => sanitizeHtml(html), [html]);

  useEffect(() => {
    if (!containerRef.current) return;

    // Find all pre elements (code blocks)
    const preElements = containerRef.current.querySelectorAll('pre');
    
    preElements.forEach((pre) => {
      // Skip if already has copy button
      if (pre.querySelector('.code-copy-btn')) return;
      
      // Make pre relative for button positioning
      pre.style.position = 'relative';
      
      // Get code content
      const codeElement = pre.querySelector('code');
      const codeText = codeElement?.textContent || pre.textContent || '';
      
      // Create copy button
      const copyBtn = document.createElement('button');
      copyBtn.className = 'code-copy-btn btn btn-sm';
      copyBtn.innerHTML = '<i class="bi bi-clipboard"></i>';
      copyBtn.title = 'Copy code';
      copyBtn.style.cssText = `
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 10;
        background-color: rgba(var(--bs-body-color-rgb), 0.12);
        border: none;
        border-radius: 4px;
        padding: 4px 8px;
        color: var(--bs-secondary-color);
        cursor: pointer;
        transition: all 0.2s;
      `;
      
      copyBtn.onmouseenter = () => {
        copyBtn.style.backgroundColor = 'rgba(var(--bs-body-color-rgb), 0.22)';
        copyBtn.style.color = 'var(--bs-body-color)';
      };
      copyBtn.onmouseleave = () => {
        copyBtn.style.backgroundColor = 'rgba(var(--bs-body-color-rgb), 0.12)';
        copyBtn.style.color = 'var(--bs-secondary-color)';
      };
      
      copyBtn.onclick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(codeText);
          copyBtn.innerHTML = '<i class="bi bi-check"></i>';
          setTimeout(() => {
            copyBtn.innerHTML = '<i class="bi bi-clipboard"></i>';
          }, 2000);
        } catch (err) {
          console.error('Failed to copy:', err);
        }
      };
      
      pre.appendChild(copyBtn);
    });
  }, [safeHtml]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={style}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
};

export default HtmlContentWithCopyButtons;
